import { message } from 'antd';
import type { RcFile } from 'antd/lib/upload/interface';

import i18n from '@/content';
import { MEGABYTE } from '@/consts';
import appConfig from '../../config/env';
import bytesToText from './bytesToText';

const BAD_STARTING_CHARS = '-+=@';
const DEFAULT_ERRORS_NAMESPACE = 'uploadFiles.messages.error';

export const validateFilename = (filename: string, nameSpace: string): void | never => {
    if (!filename) {
        message.error(i18n.t(`${nameSpace}.emptyName`));
        throw Error('emptyName');
    }
    const startingChar: string = filename[0];
    if (BAD_STARTING_CHARS.includes(startingChar)) {
        message.info(i18n.t(`${nameSpace}.invalidName`, { invalidChars: BAD_STARTING_CHARS }));
        throw Error('invalidName');
    }
};

/**
 * When the stage enforces an allowlist (via UI config / SSM), reject other extensions.
 * No-op when `allowedExtensions` is null/empty (enforcement off).
 */
export const validateFileExtension = (
    filename: string,
    allowedExtensions: string[] | null | undefined,
    nameSpace: string = DEFAULT_ERRORS_NAMESPACE,
): void | never => {
    if (!allowedExtensions?.length) {
        return;
    }
    const extension = filename.includes('.')
        ? filename.slice(filename.lastIndexOf('.') + 1).toLowerCase()
        : '';
    const allowed = new Set(allowedExtensions.map((ext) => ext.replace(/^\./, '').toLowerCase()));
    if (!extension || !allowed.has(extension)) {
        message.error(i18n.t(`${nameSpace}.unsupportedFileType`, {
            fileType: extension || '(none)',
        }));
        throw Error('unsupportedFileType');
    }
};

/** HTML `accept` value for the native file picker, or undefined when enforcement is off. */
export const buildUploadAcceptAttribute = (
    allowedExtensions: string[] | null | undefined,
): string | undefined => {
    if (!allowedExtensions?.length) {
        return undefined;
    }
    return allowedExtensions
        .map((ext) => {
            const cleaned = ext.replace(/^\./, '').toLowerCase();
            return cleaned ? `.${cleaned}` : '';
        })
        .filter(Boolean)
        .join(',');
};

export const validateFileSize = (
    file: RcFile,
    bytesLimit: number,
): void | never => {
    const fileSize = file.size;
    if (fileSize > bytesLimit) {
        message.info(i18n.t(`${DEFAULT_ERRORS_NAMESPACE}.fileSizeLimit`, { limit: bytesToText(bytesLimit) }));
        throw Error('file too big');
    }
};

const TEST_CHUNK_TO_READ_MB = 10;

export const validateFileObject = (file: RcFile): Promise<void | never> => (
    new Promise<void | never>((resolve, reject) => {
        if (file.type) {
            resolve();
        } else {
            const reader = new FileReader();
            reader.onload = () => resolve();
            reader.onerror = () => reject(reader.error);
            reader.readAsArrayBuffer(file.slice(0, TEST_CHUNK_TO_READ_MB * MEGABYTE));
        }
    })
);

const { UPLOAD_FILES_COUNT_LIMIT } = appConfig;
const MESSAGE_DURATION_SECONDS = 8;

export const validateUploadFilesCount = (filesCount: number): void | never => {
    if (UPLOAD_FILES_COUNT_LIMIT && filesCount >= UPLOAD_FILES_COUNT_LIMIT) {
        message.error({
            content: i18n.t(`${DEFAULT_ERRORS_NAMESPACE}.filesCountLimit`, { limit: UPLOAD_FILES_COUNT_LIMIT }),
            duration: MESSAGE_DURATION_SECONDS,
            key: 'validateUploadFilesCount',
        });
        throw Error('too many files');
    }
};
